import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import { useBusinessData } from "../../context/BusinessContext";
import "./PaymentReceipt.css";

// Function to format date and time
const formatDateTime = (timestamp) => {
  try {
    console.log("Original timestamp:", timestamp);

    // Handle null, undefined, or empty values
    if (!timestamp) {
      console.error("No timestamp provided");
      return new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }

    let date;
    
    // Handle different timestamp formats
    if (typeof timestamp === "string") {
      // If it's a string, check if it's a valid date string or numeric
      if (isNaN(timestamp)) {
        // Try parsing as date string (ISO format, etc.)
        date = new Date(timestamp);
      } else {
        // Parse as numeric string
        const timestampNum = parseInt(timestamp);
        // Check if it's in seconds (10 digits) or milliseconds (13 digits)
        date = timestampNum.toString().length === 10 
          ? new Date(timestampNum * 1000)  // Convert from seconds to milliseconds
          : new Date(timestampNum);        // Already in milliseconds
      }
    } else if (typeof timestamp === "number") {
      // Check if it's in seconds (10 digits) or milliseconds (13 digits)
      date = timestamp.toString().length === 10 
        ? new Date(timestamp * 1000)  // Convert from seconds to milliseconds
        : new Date(timestamp);        // Already in milliseconds
    } else {
      // Try to convert to date directly
      date = new Date(timestamp);
    }

    console.log("Created date object:", date);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date from timestamp:", timestamp);
      return new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }

    const formattedDate = new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(date);

    console.log("Formatted date:", formattedDate);
    return formattedDate;
  } catch (error) {
    console.error("Error formatting date:", error);
    // Return current date as fallback
    return new Date().toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }
};

const PaymentReceipt = ({
  transactionDetails,
  showThankYou = true,
  onMakeAnotherDonation,
  className = "",
}) => {
  const { getData, getImageUrl } = useBusinessData();

  if (!transactionDetails) return null;

  console.log("Transaction Details:", transactionDetails); // Debug log

  // Get business info
  const businessName = getData("name", "BusinessInfo");
  const businessLogo = getImageUrl("images.logo", "");
  const businessAddress = getData("address", "");
  const businessEmail = getData("email", "");
  const businessPhone = getData("phone", "");

  // Function to open receipt in new window for printing
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    
    // Get the receipt content
    const receiptContent = document.querySelector('.transaction-receipt').cloneNode(true);
    
    // Remove the action buttons from the clone
    const actionButtons = receiptContent.querySelector('.receipt-actions');
    if (actionButtons) {
      actionButtons.remove();
    }

    // Get CSS variables from root
    const rootStyles = getComputedStyle(document.documentElement);
    const cssVariables = Array.from(rootStyles).reduce((vars, prop) => {
      if (prop.startsWith('--')) {
        vars += `${prop}: ${rootStyles.getPropertyValue(prop)};\n`;
      }
      return vars;
    }, '');

    // Get all stylesheet contents from the current page
    let cssContent = '';
    const styleSheets = Array.from(document.styleSheets);
    styleSheets.forEach(styleSheet => {
      try {
        const cssRules = Array.from(styleSheet.cssRules || styleSheet.rules || []);
        cssContent += cssRules.map(rule => rule.cssText).join('\n');
      } catch (e) {
        // Skip external stylesheets that might cause CORS issues
        console.log('Could not read stylesheet:', e);
      }
    });

    printWindow.document.write(`
      <html>
        <head>
          <title>Donation Receipt - ${businessName}</title>
          <style>
            :root {
              ${cssVariables}
            }
            ${cssContent}
            
            /* Print-specific styles */
            body {
              margin: 0;
              padding: 20px;
              background: white;
            }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            ${receiptContent.outerHTML}
          </div>
          <script>
            window.onload = () => window.print();
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  return (
    <div className={`receipt-container ${className}`}>
      {showThankYou && (
        <div className="receipt-thank-you-header">
          <h2>Thank You for Your Donation!</h2>
          <p>
            {getData(
              "donation.thankYouMessage",
              "Thank you for your generous contribution! Your support makes our work possible."
            )}
          </p>
        </div>
      )}

      <div className="transaction-receipt">
        <div className="receipt-header">
          <div className="receipt-business-logo-name-container">
            {businessLogo && (
              <img
                src={businessLogo}
                alt={`${businessName} Logo`}
                className="receipt-business-logo"
              />
            )}
            <h2 className="receipt-business-name">{businessName}</h2>
          </div>

          <div className="receipt-business-info">
            {businessAddress && <p>{businessAddress}</p>}
            {businessEmail && <p>Email: {businessEmail}</p>}
            {businessPhone && <p>Phone: {businessPhone}</p>}
          </div>
        </div>

        <h3>Donation Receipt</h3>
        <div className="receipt-details">
          <div className="receipt-row">
            <div className="receipt-label">Donor Name</div>
            <div className="receipt-value">{transactionDetails.donorName}</div>
          </div>
          <div className="receipt-row">
            <div className="receipt-label">Donor Email</div>
            <div className="receipt-value">{transactionDetails.donorEmail}</div>
          </div>
          <div className="receipt-row">
            <div className="receipt-label">Donor Phone</div>
            <div className="receipt-value">{transactionDetails.donorPhone}</div>
          </div>
          <div className="receipt-row">
            <div className="receipt-label">Date & Time</div>
            <div className="receipt-value">{formatDateTime(transactionDetails.created_at)}</div>
          </div>
          <div className="receipt-row">
            <div className="receipt-label">Amount</div>
            <div className="receipt-value">
              {transactionDetails.currency} {transactionDetails.amount}
            </div>
          </div>
          <div className="receipt-row">
            <div className="receipt-label">Payment ID</div>
            <div className="receipt-value">{transactionDetails.paymentId}</div>
          </div>
          {transactionDetails.message && (
            <div className="receipt-row">
              <div className="receipt-label">Message</div>
              <div className="receipt-value">{transactionDetails.message}</div>
            </div>
          )}
          <div className="receipt-row">
            <div className="receipt-label">Status</div>
            <div className="receipt-value">{transactionDetails.status}</div>
          </div>
        </div>

        <div className="receipt-actions">
          <button className="btn btn-secondary" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} /> Print Receipt
          </button>
          {onMakeAnotherDonation && (
            <button className="btn btn-primary" onClick={onMakeAnotherDonation}>
              Make Another Donation
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentReceipt;